import { fetchApi, fetchRest, getApiBaseUrl } from '@/config/api';
import { GeneralInformationFormData, VendeurApiData } from '@/types';
import { useAuthStore } from '@/stores/auth';
import { useAdminEfpStore } from '@/stores/adminEfp';

const normalizeEmail = (email?: string | null): string | undefined => {
  if (email == null) return undefined

  const normalized = email
    .replace(/[\u00A0\u1680\u180E\u2000-\u200D\u2028\u2029\u202F\u205F\u2060\u3000\uFEFF]/g, '')
    .replace(/\s+/g, '')
    .trim()

  return normalized || undefined
}

// Récupérer les informations générales du vendeur
export async function getVendeurGeneralInfo(): Promise<VendeurApiData> {
  const query = `
    query GetVendeurGeneralInfo {
      me {
        id_vendeur
        email
        email2
        prenomContact
        nomContact
        nomSociete
        nomBoutique
        siteWeb
        commandeMini
        statut
        mea_month_delay
        siret
        tva
        vendeurData {
          data
          acheteursTemporaire
          displayColormain
          groupage
          eori
          iban
          accountTitle
        }
        adresses {
          id_adresse
          type
          adresse
          numero
          rue
          complement
          centre
          batiment
          immeuble
          ville
          codePostal
          id_pays
          pays {
            id_pays
            texte_fr
            code
          }
          telephone
          mobile
          fax
          NomContact
          Societe
        }
        vendeursLangues {
          id_vendeur_langue
          id_langue
          baseline
          description
          titre
        }
        vendeursConges {
          id_vendeur_conge
          debut_conge
          fin_conge
          dateCreation
          supprimer
        }
      }
    }
  `;

  const result = await fetchApi<{ me: VendeurApiData }>(query);
  return result.me;
}

// Variable pour éviter les appels multiples
let isUpdating = false

// Sauvegarder les informations générales du vendeur
export async function updateVendeurGeneralInfo(data: GeneralInformationFormData, images?: { logo?: File | null; banniere?: File | null; square?: File | null }): Promise<boolean> {
  if (isUpdating) {
    return false
  }

  isUpdating = true
  
  try {

    // Récupérer l'ID du vendeur depuis l'auth store
    const authStore = useAuthStore();
    const vendeurId = authStore.user?.id;
    
    if (!vendeurId) {
      throw new Error('Utilisateur non authentifié');
    }
 
    
    // Récupérer les données vendeur existantes (pour les adresses)
    const existingData = await getVendeurGeneralInfo();
    const existingAddresses = (existingData as any).adresses || [];
    
    // Mapper les données vers le format attendu par le backend
    const updateData: any = {
      id_vendeur: vendeurId,
      // Informations personnelles
      email: normalizeEmail(data.email),
      email2: normalizeEmail(data.emailSecondary),
      nomContact: data.lastName,
      prenomContact: data.firstName,
      // Informations de commande (convertir en nombre)
      commandeMini: parseInt(data.minOrder.toString()) || 0,
        site_web: data.website,
      // Informations de l'entreprise
      nomSociete: data.billingAddress.company,
      siret: data.billingAddress.siret,
      tva: data.billingAddress.tva,
      nomBoutique: data.storeAddress.storeName,
      // Statut de la boutique (convertir en nombre)
      statut: data.shop.status === 'online' ? 1 : 0,
      // Notifications (convertir en string: "1" pour true, "0" pour false)
      mel: data.shop.smsNotification ? '1' : '0',
    };

    // Récupérer les données vendeurData existantes pour ne pas écraser les champs non gérés ici
    let existingParsedDataForUpdate: any = {}
    const existingVendeurDataJsonForUpdate = (existingData as any).vendeurData?.data
    if (existingVendeurDataJsonForUpdate) {
      try {
        existingParsedDataForUpdate = JSON.parse(existingVendeurDataJsonForUpdate)
      } catch {
        existingParsedDataForUpdate = {}
      }
    }

    // Préparer les données JSON pour vendeurData en fusionnant avec les données existantes
    const vendeurDataJson = {
      ...existingParsedDataForUpdate,
      'social-url': {
        facebook: data.socialMedia.facebook,
        twitter: data.socialMedia.twitter,
        instagram: data.socialMedia.instagram,
        tiktok: data.socialMedia.tiktok,
      },
      iban: data.banking.iban,
      bic: data.banking.bic,
      // Autres paramètres shop
      displayColormain: data.shop.showMainColor ? 1 : 0,
      groupage: data.shop.smsNotification ? 1 : 0,
    };


    // Ajouter mea_month_delay dans updateData (vendeurs table)
    updateData.mea_month_delay = parseInt(data.shop.catalogVisibility) || 0;

    // Ajouter le mot de passe seulement si l'utilisateur veut le changer
    if (data.security.changePassword && data.security.newPassword) {
      updateData.password = data.security.newPassword;
    }
    
    const mutation = `
      mutation UpdateVendeur($input: UpdateVendeurInput!) {
        updateVendeur(input: $input) {
          id_vendeur
          email
          email2
          nomContact
          prenomContact
          nomSociete
          siret
          tva
          nomBoutique
          siteWeb
          commandeMini
          statut
          mel
        }
      }
    `;

    const result = await fetchApi<{ updateVendeur: any }>(
      mutation,
      { input: updateData as any }
    );

    // Mettre à jour les données JSON dans vendeurData
    if (result.updateVendeur) {
      const vendeurDataMutation = `
        mutation UpdateVendeurData($input: UpdateVendeurDataInput!) {
          updateVendeurData(input: $input) {
            id
            data
          }
        }
      `;

      const vendeurDataInput = {
        id_vendeur: vendeurId,
        data: JSON.stringify(vendeurDataJson),
        displayColormain: data.shop.showMainColor ? 1 : 0,
        groupage: data.shop.smsNotification ? 1 : 0,
      };


      await fetchApi<{ updateVendeurData: any }>(
        vendeurDataMutation,
        { input: vendeurDataInput }
      );

      // Mettre à jour les adresses
      await updateAddresses(vendeurId, data, existingAddresses);

      // Mettre à jour les congés
      await updateConges(vendeurId, data);

      // Mettre à jour les descriptions multilingues 
       await updateDescriptions(vendeurId, data);
    }
    
    // Uploader les images si elles existent
    if (images && (images.logo || images.banniere || images.square)) {
      await uploadImages(images);
    }
    
    return !!result.updateVendeur;
  } catch (error) {
    
    // Si c'est une erreur GraphQL, extraire le message détaillé
    if (error instanceof Error && error.message.includes('GraphQL')) {
      const graphqlError = error.message.match(/GraphQL error: (.+)/)?.[1] || error.message
      throw new Error(`Erreur GraphQL: ${graphqlError}`)
    }
    
    // Si c'est une erreur HTTP, extraire le statut et le message
    if (error instanceof Error && error.message.includes('HTTP')) {
      const httpError = error.message.match(/HTTP error: (\d+)(?: - (.+))?/)?.[1] || 'Unknown'
      const httpMessage = error.message.match(/HTTP error: \d+ - (.+)/)?.[1] || 'Bad Request'
      throw new Error(`Erreur HTTP ${httpError}: ${httpMessage}`)
    }
    
    const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue'
    throw new Error(`Erreur API: ${errorMessage}`)
  } finally {
    isUpdating = false
  }
}

// =============== NOUVELLES SAUVEGARDES PAR SECTION ==================
// Chaque fonction suivante persiste uniquement la section concernée.

// Sauvegarder uniquement les paramètres du compte (infos perso, entreprise, vendeurData JSON, mot de passe éventuel)
export async function saveVendeurAccountSettings(data: GeneralInformationFormData): Promise<boolean> {
  const authStore = useAuthStore()
  const vendeurId = authStore.user?.id
  if (!vendeurId) throw new Error('Utilisateur non authentifié')

  // Préparer la mutation principale updateVendeur
  // Ne pas envoyer SIRET et TVA car ils ne sont pas modifiables dans le formulaire
  const updateInput: any = {
    id_vendeur: vendeurId,
    email: normalizeEmail(data.email),
    email2: normalizeEmail(data.emailSecondary),
    nomContact: data.lastName,
    prenomContact: data.firstName,
    nomSociete: data.billingAddress.company,
    // siret et tva ne sont pas modifiables, donc on ne les envoie pas
    nomBoutique: data.storeAddress.storeName,
    site_web: data.website,
    commandeMini: parseInt(data.minOrder.toString()) || 0,
    statut: data.shop.status === 'online' ? 1 : 0,
    mel: data.shop.smsNotification ? '1' : '0',
    mea_month_delay: parseInt(data.shop.catalogVisibility) || 0,
  }
  if (data.security.changePassword && data.security.newPassword) {
    updateInput.password = data.security.newPassword
  }

  const updateMutation = `
    mutation UpdateVendeur($input: UpdateVendeurInput!) {
      updateVendeur(input: $input) { id_vendeur }
    }
  `
  await fetchApi<{ updateVendeur: { id_vendeur: number } }>(updateMutation, { input: updateInput })

  // Récupérer les données vendeurData existantes pour ne pas écraser les champs non gérés ici
  const existingDataForAccount = await getVendeurGeneralInfo()
  const existingVendeurDataJson = (existingDataForAccount as any).vendeurData?.data
  let existingParsedData: any = {}
  if (existingVendeurDataJson) {
    try {
      existingParsedData = JSON.parse(existingVendeurDataJson)
    } catch {
      existingParsedData = {}
    }
  }

  // Mettre à jour vendeurData JSON (réseaux sociaux + paramètres shop + BIC)
  // en fusionnant avec les données existantes pour préserver les champs non gérés ici
  // et champs IBAN, accountTitle et EORI directement dans la table vendeurs_data
  const vendeurDataInput = {
    id_vendeur: vendeurId,
    data: JSON.stringify({
      ...existingParsedData,
      'social-url': {
        facebook: data.socialMedia.facebook,
        twitter: data.socialMedia.twitter,
        instagram: data.socialMedia.instagram,
        tiktok: data.socialMedia.tiktok,
      },
      bic: data.banking.bic,
      displayColormain: data.shop.showMainColor ? 1 : 0,
      groupage: data.shop.smsNotification ? 1 : 0,
    }),
    displayColormain: data.shop.showMainColor ? 1 : 0,
    groupage: data.shop.smsNotification ? 1 : 0,
    iban: data.banking.iban || undefined,
    accountTitle: data.banking.accountTitle || undefined,
    eori: data.billingAddress.eori || undefined
  }
  const vendeurDataMutation = `
    mutation UpdateVendeurData($input: UpdateVendeurDataInput!) {
      updateVendeurData(input: $input) { id }
    }
  `
  await fetchApi<{ updateVendeurData: { id: number } }>(vendeurDataMutation, { input: vendeurDataInput as any })

  // Sauvegarder aussi les descriptions multilingues (baseline)
  await updateDescriptions(vendeurId, data)

  return true
}

// Sauvegarder uniquement les adresses (magasin + facturation)
export async function saveVendeurAddresses(data: GeneralInformationFormData): Promise<boolean> {
  const authStore = useAuthStore()
  const vendeurId = authStore.user?.id
  if (!vendeurId) throw new Error('Utilisateur non authentifié')

  const existingData = await getVendeurGeneralInfo()
  const existingAddresses = (existingData as any).adresses || []
  await updateAddresses(vendeurId, data, existingAddresses)

  if (
    authStore.isImpersonating &&
    useAdminEfpStore().canAccess('edit-siret-tva')
  ) {
    await fetchApi<{ updateVendeur: { id_vendeur: number } }>(
      `mutation UpdateVendeur($input: UpdateVendeurInput!) {
        updateVendeur(input: $input) { id_vendeur }
      }`,
      {
        input: {
          id_vendeur: vendeurId,
          siret: data.billingAddress.siret || '',
          tva: data.billingAddress.tva || '',
        },
      },
    )
  }

  // Sauvegarder aussi l'EORI directement dans le champ eori de vendeurs_data
  if (data.billingAddress.eori !== undefined) {
    const existingVendeurData = (existingData as any).vendeurData?.data
    let parsedData: any = {}
    if (existingVendeurData) {
      try {
        parsedData = JSON.parse(existingVendeurData)
      } catch {
        parsedData = {}
      }
    }
    
    const vendeurDataInput = {
      id_vendeur: vendeurId,
      data: JSON.stringify(parsedData),
      eori: data.billingAddress.eori || undefined
    }
    const vendeurDataMutation = `
      mutation UpdateVendeurData($input: UpdateVendeurDataInput!) {
        updateVendeurData(input: $input) { id }
      }
    `
    await fetchApi<{ updateVendeurData: { id: number } }>(vendeurDataMutation, { input: vendeurDataInput as any })
  }
  
  return true
}

// Sauvegarder uniquement les congés
export async function saveVendeurLeaves(data: GeneralInformationFormData): Promise<boolean> {
  const authStore = useAuthStore()
  const vendeurId = authStore.user?.id
  if (!vendeurId) throw new Error('Utilisateur non authentifié')
  await updateConges(vendeurId, data)
  return true
}

// Sauvegarder uniquement les descriptions multi-langues
export async function saveVendeurDescriptions(data: GeneralInformationFormData): Promise<boolean> {
  const authStore = useAuthStore()
  const vendeurId = authStore.user?.id
  if (!vendeurId) throw new Error('Utilisateur non authentifié')
  await updateDescriptions(vendeurId, data)
  return true
}

export async function translateVendeurDescriptionFromFrench(
  text: string,
): Promise<{ en: string; es: string; it: string }> {
  const response = await fetchRest(`${getApiBaseUrl()}/translate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text,
      sourceLang: 'francais',
      targetLangs: ['anglais', 'espagnol', 'italien'],
    }),
  })
  const payload = await response.json().catch(() => null)
  if (!response.ok || payload?.error) {
    throw new Error(payload?.message || payload?.error || 'Impossible de traduire la description')
  }
  return {
    en: String(payload?.anglais || ''),
    es: String(payload?.espagnol || ''),
    it: String(payload?.italien || ''),
  }
}

// Sauvegarder uniquement les images (upload)
export async function saveVendeurImages(images?: { logo?: File | null; banniere?: File | null; square?: File | null }): Promise<boolean> {
  if (!images) return true
  await uploadImages(images)
  return true
}

// Uploader les images vers le backend
async function uploadImages(images: { logo?: File | null; banniere?: File | null; square?: File | null }) {
  const uploadPromises = []

  if (images.logo) {
    uploadPromises.push(uploadImage(images.logo, 'logo'))
  }
  if (images.banniere) {
    uploadPromises.push(uploadImage(images.banniere, 'banniere'))
  }
  if (images.square) {
    uploadPromises.push(uploadImage(images.square, 'square'))
  }

  await Promise.all(uploadPromises)
}

// Uploader une image individuelle via REST (FormData + Multer)
async function uploadImage(file: File, type: 'logo' | 'banniere' | 'square') {
    const VUE_APP_API_BASE_URL = process.env.VUE_APP_API_BASE_URL
  const formData = new FormData()
  formData.append('image', file)
  formData.append('type', type == 'logo' ? 'logo-boutique' : type)

  const response = await fetchRest(`${VUE_APP_API_BASE_URL}/api/upload-image`, {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Erreur upload ${type}: ${response.status} - ${errorText}`)
  }

  const result = await response.json()
  // Le backend renvoie 200 avec { success: false, message } en cas d'erreur
  // (ex: dimensions de l'image invalides). On propage le message pour l'afficher.
  if (result && result.success === false) {
    throw new Error(result.message || `Erreur upload ${type}`)
  }

  return result
}

// Supprimer une image individuelle via REST
export async function deleteVendeurImage(type: 'banniere' | 'square' | 'logo-boutique') {
  const VUE_APP_API_BASE_URL = process.env.VUE_APP_API_BASE_URL
  const response = await fetchRest(`${VUE_APP_API_BASE_URL}/api/delete-image`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Erreur suppression ${type}: ${response.status} - ${errorText}`)
  }

  return response.json()
}

// Mettre à jour les adresses
async function updateAddresses(vendeurId: number, data: GeneralInformationFormData, existingAddresses: any[]) {
  try {
    
    const updateAddressMutation = `
      mutation UpdateAdresse($input: UpdateAdresseInput!) {
        updateAdresse(input: $input) {
          id_adresse
        }
      }
    `;

    // Trouver l'adresse magasin existante (type = 2)
    const storeAddress = existingAddresses.find(addr => addr.type === 2);
    if (storeAddress) {
      const storeAddressInput = {
        id_adresse: storeAddress.id_adresse,
        id_vendeur: vendeurId,
        type: 2,
        adresse: data.storeAddress.street,
        numero: data.storeAddress.street.match(/^\d+/)?.[0] || data.storeAddress.number,
        rue: data.storeAddress.street.replace(/^\d+\s*/, ''),
        complement: data.storeAddress.complement,
        centre: data.storeAddress.center,
        batiment: data.storeAddress.building,
        immeuble: data.storeAddress.lot,
        codePostal: data.storeAddress.postalCode,
        ville: data.storeAddress.city,
        telephone: data.storeAddress.phone,
        mobile: data.storeAddress.mobile,
        NomContact: data.storeAddress.storeName, 
        id_pays: 10, // France 
      };

      await fetchApi<{ updateAdresse: any }>(
        updateAddressMutation,
        { input: storeAddressInput }
      );
    }

    // Trouver l'adresse facturation existante (type = 3)
    const billingAddress = existingAddresses.find(addr => addr.type === 3);
    if (billingAddress) {
      const billingAddressInput = {
        id_adresse: billingAddress.id_adresse,
        id_vendeur: vendeurId,
        type: 3,
        adresse: data.billingAddress.street,
        numero: data.billingAddress.street.match(/^\d+/)?.[0] || data.billingAddress.number,
        rue: data.billingAddress.street.replace(/^\d+\s*/, ''),
        codePostal: data.billingAddress.postalCode,
        ville: data.billingAddress.city,
        telephone: data.billingAddress.phone,
        Societe: data.billingAddress.company, 
        id_pays: 10, // France
      };

      await fetchApi<{ updateAdresse: any }>(
        updateAddressMutation,
        { input: billingAddressInput }
      );
    }
  } catch (error) {
    // Erreur silencieuse - les adresses n'ont pas été mises à jour
  }
}

// Mettre à jour les congés
async function updateConges(vendeurId: number, data: GeneralInformationFormData) {
  try {
    const congesMutation = `
      mutation UpdateVendeurConges($input: UpdateVendeurCongeInput!) {
        updateVendeurConges(input: $input) {
          id_vendeur_conge
          debut_conge
          fin_conge
        }
      }
    `;

    const congesInput = {
      id_vendeur: vendeurId,
      conges: data.leaves.map(leave => ({
        id_vendeur_conge: leave.id || undefined, // undefined pour les nouveaux congés
        debut_conge: leave.startDate,
        fin_conge: leave.endDate,
        supprimer: leave.deleted ? 1 : 0 // Marquer comme supprimé si deleted = true
      }))
    };

    const result = await fetchApi<{ updateVendeurConges: any }>(
      congesMutation,
      { input: congesInput }
    );

    // Mettre à jour les IDs des nouveaux congés créés
    if (result.updateVendeurConges) {
      const newConges = data.leaves.filter(leave => !leave.id && !leave.deleted);
      const createdConges = result.updateVendeurConges.filter((conge: any) => 
        newConges.some(newConge => 
          newConge.startDate === conge.debut_conge && 
          newConge.endDate === conge.fin_conge
        )
      );
      
      // Mettre à jour les IDs dans les données locales
      newConges.forEach((newConge, index) => {
        if (createdConges[index]) {
          newConge.id = createdConges[index].id_vendeur_conge;
        }
      });
    }
  } catch (error) {
    // Erreur silencieuse - les congés n'ont pas été mis à jour
  }
}

// Mettre à jour les descriptions multilingues
async function updateDescriptions(vendeurId: number, data: GeneralInformationFormData) {
  const descriptionsMutation = `
      mutation UpdateVendeurLangue($input: UpdateVendeurLangueInput!) {
        updateVendeurLangue(input: $input) {
          id_vendeur_langue
          id_langue
          description
          baseline
        }
      }
    `;

  const descriptionsInput = {
      id_vendeur: vendeurId,
      langues: [
        { id_langue: 'fr', description: data.description.fr, baseline: data.baseline.fr },
        { id_langue: 'en', description: data.description.en, baseline: data.baseline.en },
        { id_langue: 'es', description: data.description.es, baseline: data.baseline.es },
        { id_langue: 'it', description: data.description.it, baseline: data.baseline.it },
      ]
  };

  await fetchApi<{ updateVendeurLangue: any[] }>(
    descriptionsMutation,
    { input: descriptionsInput }
  );
}

// Supprimer un congé (soft delete)
export async function deleteVendeurConge(id_vendeur_conge: number): Promise<boolean> {
  const mutation = `
    mutation UpdateVendeurConge($input: UpdateVendeurCongeInput!) {
      updateVendeurConge(input: $input) {
        id_vendeur_conge
        supprimer
      }
    }
  `;

  const input = {
    id_vendeur_conge: Number(id_vendeur_conge),
    supprimer: 1
  };

  const result = await fetchApi<{ updateVendeurConge: { id_vendeur_conge: number; supprimer: number } }>(
    mutation, 
    { input: JSON.stringify(input) }
  );

  return result.updateVendeurConge.supprimer === 1;


}

/**
 * Récupérer les infos HiPay + UBO du vendeur courant
 */
export async function getVendeurHipayBeu(): Promise<any> {
  const authStore = useAuthStore()
  const vendeurId =
      authStore.user?.id_vendeur ||
      authStore.user?.id
  if (!vendeurId) {
    throw new Error('Utilisateur non authentifié')
  }

  const query = `
    query GetHipayBeuForm($idVendeur: Int!) {
      hipayBeuForm(idVendeur: $idVendeur) {
        manager {
          lastName
          firstName
        }
        company {
          vatNumber
          iban
        }
        beneficiaries {
          id
          salutation
          lastName
          firstName
          identityCardNumber
          address
          postalCode
          city
          functionInCompany
          birthDate
          birthCity
          birthCountry
          nationality
          sharePercentage
        }
      }
    }
  `

  const result = await fetchApi<{ hipayBeuForm: any }>(query, {
    idVendeur: Number(vendeurId),
  })

  return result.hipayBeuForm
}

export async function saveVendeurHipayBeu(input: any) {
  const authStore = useAuthStore()
  const vendeurId =
      authStore.user?.id_vendeur ||
      authStore.user?.id

  if (!vendeurId) {
    throw new Error('Utilisateur non authentifié')
  }

  const mutation = `
    mutation SaveHipayBeu($idVendeur: Int!, $input: HipayBeuFormInput!) {
      saveHipayBeuForm(idVendeur: $idVendeur, input: $input) {
        manager { lastName firstName }
        company { vatNumber iban }
        beneficiaries {
          id
          salutation
          lastName
          firstName
          identityCardNumber
          address
          postalCode
          city
          functionInCompany
          birthDate
          birthCity
          birthCountry
          nationality
          sharePercentage
        }
      }
    }
  `

  const res = await fetchApi<{ saveHipayBeuForm: any }>(mutation, {
    idVendeur: Number(vendeurId),
    input,
  })

  return res.saveHipayBeuForm
}

export async function saveVendeurHipayBeneficiary(input: any) {
  const authStore = useAuthStore()
  const vendeurId =
      authStore.user?.id_vendeur ||
      authStore.user?.id

  if (!vendeurId) {
    throw new Error('Utilisateur non authentifié')
  }

  const mutation = `
    mutation ($idVendeur: Int!, $input: HipayBeuUboInput!) {
      saveHipayUbo(idVendeur: $idVendeur, input: $input) {
        id
        salutation
        lastName
        firstName
        identityCardNumber
        address
        postalCode
        city
        functionInCompany
        birthDate
        birthCity
        birthCountry
        nationality
        sharePercentage
      }
    }
  `

  const res = await fetchApi<{ saveHipayUbo: any }>(mutation, {
    idVendeur: Number(vendeurId),
    input,
  })

  return res.saveHipayUbo
}

export async function deleteVendeurHipayBeneficiary(uboId: number) {
  const authStore = useAuthStore()
  const vendeurId =
      authStore.user?.id_vendeur ||
      authStore.user?.id


  if (!vendeurId) {
    throw new Error('Utilisateur non authentifié')
  }

  const mutation = `
    mutation ($idVendeur: Int!, $uboId: Int!) {
      deleteHipayUbo(idVendeur: $idVendeur, uboId: $uboId)
    }
  `

  const res = await fetchApi<{ deleteHipayUbo: boolean }>(mutation, {
    idVendeur: Number(vendeurId),
    uboId: Number(uboId),
  })

  return res.deleteHipayUbo
}

export type HipaySubmitStep = 'all' | 'account' | 'documents' | 'bank' | 'ubo' | 'ubo_recreate'

export async function submitVendeurHipayAccount(
  step: HipaySubmitStep = 'all',
): Promise<{ success: boolean; message?: string }> {
  const authStore = useAuthStore()
  const vendeurId =
      authStore.user?.id_vendeur ||
      authStore.user?.id

  if (!vendeurId) {
    throw new Error('Utilisateur non authentifié')
  }

  const mutation = `
    mutation SubmitHipayAccount($idVendeur: Int!, $step: HipaySubmitStep) {
      submitHipayAccount(idVendeur: $idVendeur, step: $step) {
        success
        message
      }
    }
  `

  const stepEnum = step.toUpperCase() as 'ALL' | 'ACCOUNT' | 'DOCUMENTS' | 'BANK' | 'UBO' | 'UBO_RECREATE'

  const res = await fetchApi<{ submitHipayAccount: { success: boolean; message?: string } }>(mutation, {
    idVendeur: Number(vendeurId),
    step: stepEnum,
  })

  return res.submitHipayAccount
}

export async function deleteVendeurHipayUboStatement(): Promise<{
  success: boolean
  message?: string
}> {
  const authStore = useAuthStore()
  const vendeurId =
      authStore.user?.id_vendeur ||
      authStore.user?.id

  if (!vendeurId) {
    throw new Error('Utilisateur non authentifié')
  }

  const mutation = `
    mutation DeleteHipayUboStatement($idVendeur: Int!) {
      deleteHipayUboStatement(idVendeur: $idVendeur) {
        success
        message
      }
    }
  `

  const res = await fetchApi<{
    deleteHipayUboStatement: { success: boolean; message?: string }
  }>(mutation, {
    idVendeur: Number(vendeurId),
  })

  return res.deleteHipayUboStatement
}

//sauvegarder dans backend
export async function uploadHipayFile(
    file: File,
    type: string,
    options?: { beneficiaryId?: number },
) {
  const authStore = useAuthStore()
  const vendeurId =
      authStore.user?.id_vendeur ||
      authStore.user?.id ||
      undefined

  const formData = new FormData()

  formData.append('type', type)

  // envoiyer a backend ./upload/infoClientHipay/<vendeurId>
  if (vendeurId) {
    formData.append('vendeurId', String(vendeurId))
  }

  // si bénéficiaire
  if (options?.beneficiaryId != null) {
    formData.append('beneficiaryId', String(options.beneficiaryId))
  }
  formData.append('file', file)
   const baseApi = process.env.VUE_APP_API_BASE_URL
  // The base API env points to server (e.g. http://localhost:3002).
  // Backend exposes the upload under Nest controller POST /api/upload-hipay
  // (see backend-backoffice-seller/src/upload/upload-hipay.controller.ts).
  // Build the correct endpoint and normalize trailing slashes.
  const endpoint = `${(baseApi || '').replace(/\/+$/, '')}/api/upload-hipay`;
  const res = await fetchRest(endpoint, {
    method: 'POST',
    body: formData,
  })

  if (!res.ok) {
    const txt = await res.text()
    throw new Error('Upload HiPay échoué: ' + txt)
  }

  return res.json()
}

// Récupérer les fichiers HiPay déjà téléversés (pour l'affichage après rafraîchissement)
export async function getHipayFiles() {
  const authStore = useAuthStore()
  const vendeurId =
    authStore.user?.id_vendeur ||
    authStore.user?.id ||
    undefined

  const baseApi = process.env.VUE_APP_API_BASE_URL
  const endpoint = `${(baseApi || '').replace(/\/\/+$/,'')}/api/hipay-files${vendeurId ? `?vendeurId=${encodeURIComponent(String(vendeurId))}` : ''}`

  const res = await fetchRest(endpoint, {
    method: 'GET',
  })

  if (!res.ok) {
    const txt = await res.text()
    throw new Error('Récupération des fichiers HiPay échouée: ' + txt)
  }

  const data = await res.json()

  // Les anciens fichiers wapi utilisent une URL relative au backend.
  const makeAbs = (url?: string) => {
    if (!url || /^https?:\/\//i.test(url)) return url
    return `${(baseApi || '').replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`
  }

  try {
    if (data?.manager) {
      data.manager.identityFront = makeAbs(data.manager.identityFront)
      data.manager.identityBack = makeAbs(data.manager.identityBack)
    }
    if (data?.company) {
      data.company.rib = makeAbs(data.company.rib)
      data.company.kbis = makeAbs(data.company.kbis)
      data.company.signedArticles = makeAbs(data.company.signedArticles)
    }
    if (data?.beneficiaries) {
      for (const key of Object.keys(data.beneficiaries)) {
        const entry = data.beneficiaries[key]
        if (entry) {
          entry.front = makeAbs(entry.front)
          entry.back = makeAbs(entry.back)
        }
      }
    }
  } catch (e) {
    // If normalization fails, just return raw data
  }

  return data
}
